import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/data';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(
    () =>
      article
        ? articles.filter((a) => a.categoryId === article.categoryId && a.slug !== article.slug).slice(0, 3)
        : [],
    [article]
  );

  if (!article) {
    return (
      <main>
        <div className="container-site py-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-file-unknow-line text-2xl text-foreground-300"></i>
          </div>
          <h2 className="text-xl font-semibold font-heading text-foreground-800 mb-2">文章未找到</h2>
          <p className="text-sm text-foreground-500 mb-6">该文章可能已被移除或链接无效</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-all duration-200 whitespace-nowrap text-sm"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.publishDate,
            author: { '@type': 'Organization', name: '鲍鱼tv跳转导航' },
            publisher: { '@type': 'Organization', name: '鲍鱼tv跳转导航' },
            url: `https://baoyutv-tiaozhuan.com/article/${article.slug}`,
          }),
        }}
      />

      <main>
        <section className="py-8 md:py-12 bg-background-100/70">
          <div className="container-site">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: article.categoryName, path: `/category/${article.categoryId}` },
                { label: article.title },
              ]}
            />
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="container-site">
            <div className="max-w-3xl mx-auto">
              {/* Article Header */}
              <header className="mb-8">
                <Link
                  to={`/category/${article.categoryId}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-accent-100 text-accent-700 rounded-full text-xs font-medium mb-4 hover:bg-accent-200 transition-colors duration-200 whitespace-nowrap"
                >
                  <i className="ri-folder-line"></i>
                  {article.categoryName}
                </Link>
                <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    发布于 {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    阅读时间 {article.readTime}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
              </header>

              {/* Article Content */}
              <div
                className="prose-custom text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
              <style>{`
                .prose-custom h2 { font-size: 1.5rem; font-weight: 700; font-family: var(--font-heading); margin-top: 2rem; margin-bottom: 1rem; color: oklch(var(--foreground-900)); }
                .prose-custom h3 { font-size: 1.2rem; font-weight: 600; font-family: var(--font-heading); margin-top: 1.5rem; margin-bottom: 0.75rem; color: oklch(var(--foreground-850)); }
                .prose-custom p { margin-bottom: 1rem; font-size: 0.95rem; line-height: 1.8; }
                .prose-custom ul, .prose-custom ol { margin-bottom: 1rem; padding-left: 1.5rem; }
                .prose-custom li { margin-bottom: 0.5rem; font-size: 0.95rem; line-height: 1.7; }
                .prose-custom strong { color: oklch(var(--foreground-900)); font-weight: 600; }
              `}</style>

              {/* Article Footer */}
              <footer className="mt-10 pt-6 border-t border-background-200/70">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Link
                    to={`/category/${article.categoryId}`}
                    className="inline-flex items-center gap-1.5 text-sm text-accent-600 hover:text-accent-700 font-medium transition-colors duration-200 whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回{article.categoryName}分类
                  </Link>
                  <div className="flex items-center gap-3 text-foreground-400">
                    <span className="text-xs">分享到：</span>
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}`}
                      target="_blank"
                      rel="nofollow noopener"
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors duration-200"
                      aria-label="分享到Twitter"
                    >
                      <i className="ri-twitter-x-line"></i>
                    </a>
                    <a
                      href={`https://service.weibo.com/share/share.php?title=${encodeURIComponent(article.title)}`}
                      target="_blank"
                      rel="nofollow noopener"
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors duration-200"
                      aria-label="分享到微博"
                    >
                      <i className="ri-weibo-line"></i>
                    </a>
                  </div>
                </div>
              </footer>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100/70" aria-label="相关文章">
            <div className="container-site">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} {...rel} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}